import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import ReactTable from 'react-table';
import {CSVLink} from 'react-csv';
import ReactToPrint from 'react-to-print';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import {
    convertDate,
    convertDateTo24HourString,
    sortCurrency,
    toCurrency
} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from '../shared/tableFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import DialogPaymentSeq from './DialogPaymentSeq';
import DialogSaleReceipt from './DialogSaleReceipt';
import DialogSaleSeq from './DialogSaleSeq';
import FuseTheme from '../global/theme';
import {commonStyles} from '../shared/styles';
import {tableStyles} from '../shared/styles-table';
import '../shared/react-table.css';
import CircularProgress from 'material-ui/CircularProgress';
import Dialog from 'material-ui/Dialog';
import RaisedButton from 'material-ui/RaisedButton';
import FlatButton from "material-ui/FlatButton";
import DetailIcon from 'material-ui/svg-icons/image/remove-red-eye';
import DetailTableIcon from "material-ui/svg-icons/communication/location-on";
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import TableIcon from 'material-ui/svg-icons/action/receipt';
import Config from '../messages';

const styles = {
    headerButton: {
        display: 'inline-block',
        color: FuseTheme.palette.alternateTextColor,
        margin: '0 10px 0 0',
    },
    headerButtonContainer: {
        display: 'inline-block',
        position: 'absolute',
        top: '50%',
        right: '1em',
        transform: 'translateY(-50%)'
    },
    headerButtonIcon: {
        display: 'inline-block',
        color: FuseTheme.palette.alternateTextColor,
        margin: '0 6px 0 8px',
        verticalAlign: 'middle',
    },
    headerButtonLink: {
        display: 'inline-block',
        fontWeight: 500,
        color: FuseTheme.palette.alternateTextColor,
        textTransform: 'uppercase',
        textDecoration: 'none',
        margin: '0 12px 0 0'
    },
    summaryIcon: {
        fill: FuseTheme.palette.accent2Color,
        width: 36,
        height: 36,
        margin: '0 10px 0 0',
        verticalAlign: 'middle'
    },
    tableHeaderTitle: {fontWeight: '400', color: FuseTheme.palette.alternateTextColor, margin: '0'},
    tableContainer: {maxWidth: '100%', overflowX: 'auto'},
    thGroupMin: {width: 220, minWidth: 220},
    thSmall: {fontSize: 11},
    thLast: {borderRight: '1px solid #bdbdbd'},
    tdMax: {width: 90, maxWidth: 90},
    tdMin180: {width: 180, minWidth: 180},
    footer: {fontSize: 13, fontWeight: 600, textAlign: 'right'},
    left: {textAlign: 'left'},
    center: {textAlign: 'center'},
    right: {textAlign: 'right'},
    wrapText: {wordWrap: 'break-word'},
    hide: {display: 'none'},
    printBtn: {fontWeight: 'bold', cursor: 'pointer'},
    button: {color: FuseTheme.palette.primary3Color},
    linkDefault: {
        cursor: 'pointer',
        color: FuseTheme.palette.primary1Color
    },
    dialogContent: {maxWidth: '70%'},
    tableIconButton: {
        display: 'inline-block',
        width: 40,
        height: 26,
        color: FuseTheme.palette.primary1Color,
        padding: '0 0 0 10px',
        verticalAlign: 'middle',
        cursor: 'pointer'
    },
    disabledTableIconButton: {
        display: 'inline-block',
        width: 40,
        height: 26,
        color: FuseTheme.palette.disabledColor,
        padding: '0 0 0 10px',
        verticalAlign: 'middle',
        cursor: 'pointer',
        pointerEvents: 'none',
        opacity: '0.7'
    },
    paymentLogHeader: {
        background: 'rgb(117, 117, 117)',
        padding: '.5em 1em',
        color: '#fff'
    },
    transactionRow: {
        display: 'flex',
        width: '100%',
        borderBottom: '1px solid rgb(117, 117, 117)',
    },
    rowBg: {
        background: '#f5f5f5'
    },
    transactionTitle: {
        flex: 1,
        fontWeight: 600,
        padding: '0.5em 1em',
        borderRight: '1px solid rgb(117, 117, 117)'
    },
    transactionValue: {
        flex: 2,
        padding: '0.5em 1em'
    },
    flexContainerRight: {
        display: 'flex',
        justifyContent: 'flex-end',
    }
};

/**
 * Class for retrieving and displaying Transaction Log Summary in a single table
 *
 */
class TransactionLogReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            allowLocationSelection: true,
            detailComplete: false,
            detailList: [],
            detailLoading: false,
            detailTableData: [],
            detailTableHeaders: [
                {label: 'Payment Id', key: 'payment_seq'},
                {label: 'Sale Id', key: 'sale_seq'},
                {label: 'Transaction Date', key: 'transaction_date_string'},
                {label: 'Register ID', key: 'register_id'},
                {label: 'Payment Type', key: 'transaction_type'},
                {label: 'Transaction', key: 'transaction'},
                {label: 'Currency', key: 'currency'},
                {label: 'Price', key: 'subtotal'},
                {label: 'Tax', key: 'tax'},
                {label: 'Card Type', key: 'card_type'},
                {label: 'Last 4', key: 'card_last4'},
                {label: 'Card Data Source', key: 'card_data_source'},
                {label: 'PN Ref', key: 'pn_ref'},
                {label: 'Auth', key: 'authcode'},
                {label: 'Mode', key: 'mode'},
            ],
            detailLocation: {
                id: null,
                name: null,
                dba_name: null,
                selected: false,
                city: null,
                state: null,
                zip_code: null
            },
            reportName: 'Transaction Log',
            summaryList: [],
            summaryTableData: [],
            summaryTableHeaders: [
                {label: 'Location ID', key: 'location_id'},
                {label: 'Location Name', key: 'location_name'},
                {label: 'State', key: 'state'},
                {label: 'DBA', key: 'dba_name'},
                {label: 'Zip Code', key: 'zip_code'},
            ],
            message: null,
            summaryComplete: false,
            transactionData: null,
            transactionDialogOpen: false,
            creditList: [],
            creditTableData: [],
            saleTransactionData: null,
            saleDialogOpen: false,
            salePaymentsList: [],
            saleCreditsList: [],
            saleProductList: [],
            saleProductReturnedList: [],
            saleDiscountsList: [],
        };
        this.detailPosition = null;
        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleSortOrFilterChange = this.handleSortOrFilterChange.bind(this);
    }

    componentWillMount() {
        this.fetchSalesData(null);
    }

    componentWillReceiveProps(nextProps) {
        let clearTable = false;
        if (
            nextProps.startDate !== this.props.startDate ||
            nextProps.startTime !== this.props.startTime ||
            nextProps.endDate !== this.props.endDate ||
            nextProps.endTime !== this.props.endTime ||
            nextProps.offset !== this.props.offset
        ) {
            clearTable = true;
        }
        if (clearTable) {
            this.setState({
                detailList: [],
                detailLoading: false,
                detailTableData: [],
                detailComplete: false,
                summaryList: [],
                summaryTableData: [],
                summaryComplete: false,
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.startDate !== this.props.startDate ||
            prevProps.startTime !== this.props.startTime ||
            prevProps.endDate !== this.props.endDate ||
            prevProps.endTime !== this.props.endTime ||
            prevProps.offset !== this.props.offset
        ) {
            this.fetchSalesData(null);
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Get Sales data for a specific date range
     * @param locationId
     */
    fetchSalesData(locationId) {
        // Assemble the URI
        const params = {
            start_date: this.props.startDate,
            start_time: this.props.startTime,
            end_date: this.props.endDate,
            end_time: this.props.endTime,
            offset: this.props.offset
        };

        let resource;
        if (locationId) {
            params.location_id = locationId;
            resource = reportsResources.TransactionLogLocationByDate.GET_LIST(params);
        } else {
            resource = reportsResources.TransactionLogByDate.GET_LIST(params);
        }

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processSalesData(locationId, result.data.payload);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + this.state.reportName + '.';
            if (error.response) {
                // Server response outside 2xx
                if (error.response.data && error.response.data.error) {
                    message = Config.fuse.error.error + this.state.reportName + ': ' + error.response.data.error;
                }
            } else if (error.request) {
                // No response
                message = Config.fuse.error.no_response + this.state.reportName + '.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                summaryList: [],
                summaryTableData: [],
                summaryComplete: false,
            }, () => {
                if (typeof this.props.onError === 'function') {
                    this.props.onError(message);
                }
            });
        });
    }

    /**
     * Fetch Transaction Data
     * @param seq
     * @param type
     */
    fetchTransactionData(seq, type) {
        let resource = reportsResources.TransactionLogPaymentBySeq.GET_LIST(seq);

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processTransactionData(type, result.data.payload);
            }
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
                console.log(error.response.data);
            } else if (error.request) {
                // No response
                console.log(error.request);
            } else {
                console.log('Error', error.message);
            }
        });
    }

    /**
     * Fetch Sale Data
     * @param seq
     * @param type
     */
    fetchSaleTransactionData(seq, type) {
        let resource = reportsResources.SaleLogBySeq.GET_LIST(seq);

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processSaleTransactionData(type, result.data.payload);
            }
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
                console.log(error.response.data);
            } else if (error.request) {
                // No response
                console.log(error.request);
            } else {
                console.log('Error', error.message);
            }
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store transaction/type results
     *
     * @param locationId
     * @param data
     */
    processSalesData(locationId, data) {

        const emptyCurrency = toCurrency(0);
        let summaryList = [];
        let detailList = [];

        if (data.aggregations) {
            if (!locationId) {
                if (data.aggregations.by_location && data.aggregations.by_location.buckets) {
                    let results = data.aggregations.by_location.buckets;
                    let totalResults = results.length;
                    if (totalResults) {
                        // Loop on the results
                        for (let s = 0; s < totalResults; s++) {
                            let locationResult = results[s];
                            let location = locationResult.column_names.hits.hits[0]._source.rxtxMsg.payload.location;
                            let dbaName = location.dba.name;
                            let zipCode = location.zipcode;
                            const locationRow = {
                                location_id: (location.id) ? location.id : '----',
                                location_name: (location.name) ? location.name : '----',
                                city: (location.city) ? location.city : '----',
                                state: (location.state) ? location.state : '(None)',
                                dba_name: (dbaName) ? dbaName : '(None)',
                                zip_code: (zipCode) ? zipCode : '(None)'
                            };
                            summaryList.push(locationRow);
                        }
                    }

                }
                // Finalize table data
                this.setState({
                    summaryList: summaryList,
                    summaryTableData: summaryList,
                    summaryComplete: true,
                });
                this.props.onSummaryComplete(true);
            }
            if (locationId) {
                if (data.aggregations.by_transaction.buckets) {
                    let results = data.aggregations.by_transaction.buckets;
                    let totalResults = results.length;
                    if (totalResults) {
                        for (let d = 0; d < totalResults; d++) {
                            let detailResult = results[d];
                            let details = detailResult.column_name.hits.hits[0]._source.rxtxMsg.payload;
                            let tDate = (details.transaction_date) ? details.transaction_date : 'No Date';
                            let tDateString = (details.transaction_date) ? convertDate(details.transaction_date, 'display', true, true, this.props.offset, true) : 'No Date';
                            let tDate24HString = (details.transaction_date) ? convertDateTo24HourString(details.transaction_date, true, this.props.offset) : 'No Date';

                            let saleTax = (details.tax) ? Number(details.tax) : 0;
                            let additionalTax = (details.additional_tax_total) ? Number(details.additional_tax_total) : 0;
                            let totalTaxes = saleTax + additionalTax;
                            const detailRow = {
                                payment_seq: (details.payment_seq) ? details.payment_seq : '----',
                                sale_seq: (details.sale_seq) ? details.sale_seq : '----',
                                location_id: (locationId) ? locationId : '----',
                                transaction_date: tDate,
                                transaction_date_string: tDateString,
                                transaction_date_24h_string: tDate24HString,
                                transaction_type: (details.transaction_type) ? details.transaction_type : '----',
                                transaction: (details.transaction) ? details.transaction : '----',
                                currency: (details.currency) ? (toCurrency(details.currency / 100)) : emptyCurrency,
                                subtotal: (details.subtotal) ? (toCurrency(details.subtotal / 100)) : emptyCurrency,
                                tax: (totalTaxes) ? (toCurrency(totalTaxes / 100)) : emptyCurrency,
                                card_type: (details.card_type) ? details.card_type : '----',
                                card_last4: (details.card_last4) ? details.card_last4 : '----',
                                card_data_source: (details.card_data_source) ? details.card_data_source : '----',
                                pn_ref: (details.pn_ref) ? details.pn_ref : '----',
                                authcode: (details.authcode) ? details.authcode : '----',
                                mode: (details.mode) ? details.mode : '----',
                                register_id: (details.client && details.client.register) ? details.client.register : '--'
                            };
                            detailList.push(detailRow);
                        }
                    }
                }
                this.setState({
                        detailList: detailList,
                        detailLoading: false,
                        detailTableData: detailList,
                        detailComplete: true,
                    },
                    () => {
                        this.scrollToDetailTable();
                    });
            }
        }
    }

    processTransactionData(type, data) {
        const emptyCurrency = toCurrency(0);
        let creditList = [];

        if (data.hits.hits[0]) {

            for (let c = 0; c < data.hits.hits.length; c++) {
                let details = data.hits.hits[c]._source.rxtxMsg.payload;
                let tDateString = (details.transaction_date) ? convertDate(details.transaction_date, 'display', true, true, this.props.offset, true) : 'No Date';
                let tDate24HString = (details.transaction_date) ? convertDateTo24HourString(details.transaction_date, true, this.props.offset) : 'No Date';

                if (details.transaction === 'payment') {
                    let salesTax = (details.tax) ? Number(details.tax) : 0;
                    let additionalTax = (details.additional_tax_total) ? Number(details.additional_tax_total) : 0;
                    let totalTaxes = salesTax + additionalTax;
                    const detailRow = {
                        location: (details.client.location) ? details.client.location : null,
                        register: (details.client.register) ? details.client.register : null,
                        currency: (details.currency) ? toCurrency(details.currency / 100) : emptyCurrency,
                        mode: (details.mode) ? details.mode : '----',
                        payment_seq: (details.payment_seq) ? details.payment_seq : null,
                        sale_seq: (details.sale_seq) ? details.sale_seq : null,
                        subtotal: (details.subtotal) ? toCurrency(details.subtotal / 100) : emptyCurrency,
                        tax: (totalTaxes) ? toCurrency(totalTaxes / 100) : emptyCurrency,
                        transaction: (details.transaction) ? details.transaction : null,
                        transaction_date: (details.transaction_date) ? details.transaction_date : 'No Date',
                        transaction_date_string: tDateString,
                        transaction_date_24h_string: tDate24HString,
                        transaction_type: (details.transaction_type) ? details.transaction_type : '----',
                        authcode: (details.authcode) ? details.authcode : null,
                        card_data_source: (details.card_data_source) ? details.card_data_source : null,
                        card_first_name: (details.card_first_name) ? details.card_first_name : null,
                        card_last_name: (details.card_last_name) ? details.card_last_name : null,
                        card_last4: (details.card_last4) ? details.card_last4 : null,
                        card_status: (details.card_status) ? 'True' : null,
                        card_type: (details.card_type) ? details.card_type : null,
                        pn_ref: (details.pn_ref) ? details.pn_ref : null,
                        message: (details.message) ? details.message : null,
                        response_message: (details.response_message) ? details.response_message : null,
                        code: (details.code) ? details.code : null,
                        ext_data: (details.ext_data) ? details.ext_data : null
                    };

                    this.setState({
                        transactionData: detailRow,
                    });

                } else {
                    let subtotal = parseFloat(toCurrency(details.subtotal / 100));
                    let tax = parseFloat(toCurrency(details.tax / 100));
                    let amount = (subtotal + tax).toFixed(2);
                    let salesTax = (details.tax) ? Number(details.tax) : 0;
                    let additionalTax = (details.additional_tax_total) ? Number(details.additional_tax_total) : 0;
                    let totalTaxes = salesTax + additionalTax;
                    const creditRow = {
                        id: (details.credit_seq) ? details.credit_seq : '----',
                        currency: (details.currency) ? toCurrency(details.currency / 100) : emptyCurrency,
                        location: (details.client.location) ? details.client.location : '----',
                        register: (details.client.register) ? details.client.register : '----',
                        amount: (amount) ? amount : emptyCurrency,
                        subtotal: (details.subtotal) ? toCurrency(details.subtotal / 100) : emptyCurrency,
                        tax: (totalTaxes) ? toCurrency(totalTaxes / 100) : emptyCurrency,
                        mode: (details.mode) ? details.mode : '----',
                        transaction_date: (details.transaction_date) ? details.transaction_date : 'No Date',
                        transaction_date_string: tDateString,
                        transaction_date_24h_string: tDate24HString
                    };

                    creditList.push(creditRow);

                    this.setState({
                        creditList: creditList,
                        creditTableData: creditList
                    });
                }
            }
        }
    }

    processSaleTransactionData(type, data) {
        const emptyCurrency = toCurrency(0);
        let salePaymentsList = [];
        let saleCreditsList = [];
        let saleProductList = [];
        let saleProductReturnedList = [];
        let saleDiscountsList = [];
        let divisor = 100;

        let details = data.hits.hits[0]._source.rxtxMsg.payload;
        let tDateString = (details.transaction_date) ? convertDate(details.transaction_date, 'display', true, true, this.props.offset, true) : 'No Date';
        let tDate24HString = (details.transaction_date) ? convertDateTo24HourString(details.transaction_date, true, this.props.offset) : 'No Date';

        if (details) {
            const detailRow = {
                dba_email: (details.location && details.location.dba) ? details.location.dba.email : '',
                dba_name: (details.location && details.location.dba) ? details.location.dba.name : '',
                device: (details.client.device) ? details.client.device : '----',
                initial_net: (details.initial_net) ? (toCurrency(details.initial_net / divisor)) : emptyCurrency,
                initial_tax: (details.initial_tax) ? (toCurrency(details.initial_tax / divisor)) : emptyCurrency,
                initial_additional_taxes: (details.initial_additional_taxes) ? (toCurrency(details.initial_additional_taxes / divisor)) : emptyCurrency,
                initial_total: (details.initial_total) ? (toCurrency(details.initial_total / divisor)) : emptyCurrency,
                location: (details.client.location) ? details.client.location : '----',
                location_address: (details.location.address) ? details.location.address : '',
                location_city: (details.location.city) ? details.location.city : '',
                location_telephone: (details.location.telephone) ? details.location.telephone : '',
                location_state: (details.location.state) ? details.location.state : '',
                location_tax_rate: (details.location.tax_rate) ? details.location.tax_rate.toString() : '0',
                location_zipcode: (details.location.zipcode) ? details.location.zipcode : '',
                loyalty_id: (details.loyalty_customer && details.loyalty_customer.id) ? details.loyalty_customer.id : '',
                register: (details.client.register) ? details.client.register : '----',
                user: (details.client.user) ? details.client.user : '----',
                version: (details.client.version) ? details.client.version : '----',
                mode: (details.mode) ? details.mode : '----',
                seq: (details.seq) ? details.seq : '----',
                transaction_date_string: tDateString,
                transaction_date_24h_string: tDate24HString
            };

            if (details.payments) {
                let totalPayments = details.payments.length;
                for (let i = 0; i < totalPayments; i++) {
                    let paymentResult = details.payments[i];
                    const paymentRow = {
                        seq: (paymentResult.seq) ? paymentResult.seq : '----',
                        type: (paymentResult.type) ? paymentResult.type : '----',
                        card_type: (paymentResult.card_type) ? paymentResult.card_type : '----',
                        card_last4: (paymentResult.card_last4) ? paymentResult.card_last4 : '----',
                        card_data_source: (paymentResult.card_data_source) ? paymentResult.card_data_source : '----',
                        pn_ref: (paymentResult.pn_ref) ? paymentResult.pn_ref : '----',
                        authcode: (paymentResult.authcode) ? paymentResult.authcode : '----',
                        mode: (paymentResult.mode) ? paymentResult.mode : '----',
                        subtotal: (paymentResult.subtotal) ? (toCurrency(paymentResult.subtotal / divisor)) : emptyCurrency,
                        tax: (paymentResult.tax) ? (toCurrency(paymentResult.tax / divisor)) : emptyCurrency,
                        additional_tax_total: (paymentResult.additional_tax_total) ? (toCurrency(paymentResult.additional_tax_total / divisor)) : emptyCurrency,
                        additional_taxes: (paymentResult.additional_taxes) ? paymentResult.additional_taxes : null,
                        currency: (paymentResult.currency)
                            ? (paymentResult.type === 'loyalty_points')
                                ? (toCurrency(paymentResult.currency / 1000))
                                : (toCurrency(paymentResult.currency / divisor))
                            : emptyCurrency
                    };
                    salePaymentsList.push(paymentRow);
                }
                // Order payments: Loyalty Points, Cash, Credit
                const paymentSort = ['loyalty_points', 'cash', 'out_of_scope'];
                let paymentObj = {};
                paymentSort.forEach(function (a, i) {
                    paymentObj[a] = i + 1;
                });
                salePaymentsList.sort(function (a, b) {
                    return (paymentObj[a.type] || Infinity) - (paymentObj[b.type] || Infinity);
                });
                this.setState({
                    salePaymentsList: salePaymentsList
                });
            }

            if (details.credits) {
                let totalCredits = details.credits.length;
                for (let i = 0; i < totalCredits; i++) {
                    let creditResult = details.credits[i];
                    let tDateString = (creditResult.credit_date) ? convertDate(creditResult.credit_date, 'display', true, true, this.props.offset, true) : 'No Date';
                    const creditRow = {
                        seq: (creditResult.seq) ? creditResult.seq : '----',
                        payment_seq: (creditResult.payment_seq) ? creditResult.payment_seq : '----',
                        type: (creditResult.type) ? creditResult.type : '----',
                        credit_date: tDateString,
                        subtotal: (creditResult.subtotal) ? (toCurrency(creditResult.subtotal / 100)) : emptyCurrency,
                        tax: (creditResult.tax) ? (toCurrency(creditResult.tax / 100)) : emptyCurrency,
                        additional_tax_total: (creditResult.additional_tax_total) ? (toCurrency(creditResult.additional_tax_total / divisor)) : emptyCurrency,
                        currency: (creditResult.currency) ? (toCurrency(creditResult.currency / 100)) : emptyCurrency,
                        card_type: (creditResult.card_type) ? creditResult.card_type : '----',
                        card_last4: (creditResult.card_last4) ? creditResult.card_last4 : '----',
                        card_data_source: (creditResult.card_data_source) ? creditResult.card_data_source : '----',
                        pn_ref: (creditResult.pn_ref) ? creditResult.pn_ref : '----',
                        authcode: (creditResult.authcode) ? creditResult.authcode : '----',
                        mode: (creditResult.mode) ? creditResult.mode : '----',
                        status: (creditResult.status) ? "True" : '----'

                    };
                    saleCreditsList.push(creditRow);
                }
                this.setState({
                    saleCreditsList: saleCreditsList
                });
            }

            let discountTotal = 0;
            if (details.discounts) {
                let totalDiscounts = details.discounts.length;
                for (let i = 0; i < totalDiscounts; i++) {
                    let discountResult = details.discounts[i];
                    const discountRow = {
                        code: (discountResult.code) ? discountResult.code : '----',
                        name: (discountResult.name) ? discountResult.name : '----',
                        discount_each: (discountResult.discount_each) ? (toCurrency(discountResult.discount_each / 100)) : emptyCurrency
                    };
                    saleDiscountsList.push(discountRow);
                    discountTotal += (discountResult.discount_each) ? parseInt(discountResult.discount_each, 10) : 0;
                }
                this.setState({
                    saleDiscountsList: saleDiscountsList
                });
            }

            if (details.product_units) {
                let totalProducts = details.product_units.length;
                for (let i = 0; i < totalProducts; i++) {
                    let productResult = details.product_units[i];
                    // Use Display Price until calculations are adjusted
                    let productQty = (productResult.qty) ? productResult.qty : 0;
                    let displayPrice = (productResult.display_price) ? productResult.display_price : 0;
                    let displaySubtotal = (displayPrice * productQty);
                    const productRow = {
                        product_name: (productResult.product_name) ? productResult.product_name : '----',
                        product_unit_id: (productResult.product_unit_id) ? productResult.product_unit_id : '----',
                        product_unit_type: (productResult.product_unit_type) ? productResult.product_unit_type : '----',
                        qty: (productResult.qty) ? productResult.qty : '----',
                        price_each: (productResult.price_each) ? (toCurrency(productResult.price_each / divisor)) : emptyCurrency,
                        price_each_display: (displayPrice) ? toCurrency(displayPrice) : emptyCurrency,
                        price_subtotal: (productResult.price_subtotal) ? (toCurrency(productResult.price_subtotal / divisor)) : emptyCurrency,
                        price_subtotal_display: (displaySubtotal) ? toCurrency(displaySubtotal) : emptyCurrency,
                        price_total: (productResult.price_total) ? (toCurrency(productResult.price_total / divisor)) : emptyCurrency,
                    };
                    saleProductList.push(productRow);
                }
                this.setState({
                    saleProductList: saleProductList
                });
            }

            if (details.product_units_returned) {
                let totalProductsReturned = details.product_units_returned.length;
                for (let i = 0; i < totalProductsReturned; i++) {
                    let productReturnedResult = details.product_units_returned[i];
                    const productReturnedRow = {
                        product_unit_id: (productReturnedResult.product_unit_id) ? productReturnedResult.product_unit_id : '----',
                        product_name: (productReturnedResult.product_name) ? productReturnedResult.product_name : '----',
                        product_unit_type: (productReturnedResult.product_unit_type) ? productReturnedResult.product_unit_type : '----',
                        qty: (productReturnedResult.qty) ? productReturnedResult.qty : '----',
                    };
                    saleProductReturnedList.push(productReturnedRow);
                }
                this.setState({
                    saleProductReturnedList: saleProductReturnedList
                });
            }

            detailRow.discount_total = (discountTotal) ? (toCurrency(discountTotal / divisor)) : emptyCurrency;
            detailRow.discount_total_string = detailRow.discount_total;
            if (discountTotal < 0) {
                const discountAmount = discountTotal.toString().substr(1);
                detailRow.discount_total_string = '-$' + toCurrency(parseInt(discountAmount, 10) / 100);
            }
            this.setState({
                saleTransactionData: detailRow
            });
        }
    }

    /* Table Building ------------------------------ */
    handleLocationSelect(event, row) {
        const location = {
            id: row.location_id,
            name: row.location_name,
            dba_name: row.dba_name,
            selected: true,
            city: row.city,
            state: row.state,
            zip_code: row.zip_code
        };
        this.setState({
            allowLocationSelection: true,
            detailLocation: location,
            detailLoading: true
        }, () => {
            this.scrollToDetailTable();
            this.fetchSalesData(location.id);
        });
    }

    scrollToDetailTable() {
        window.scrollTo(0, this.detailPosition.offsetTop);
    }

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param table
     */
    handleSortOrFilterChange(event, table) {
        const tableData = getTableState(table);
        if (tableData && tableData.length > 0) {
            this.setState({
                summaryTableData: tableData
            });
        }
    }

    /**
     * Open/Close Dialog
     * @param e
     * @param seq
     * @param type
     */
    handleOpen = (e, seq, type) => {
        this.setState({
                transactionDialogOpen: true
            },
            () => {
                this.fetchTransactionData(seq, type);
            });
    };

    handleSaleOpen = (e, seq, type) => {
        this.setState({
                saleDialogOpen: true
            },
            () => {
                this.fetchSaleTransactionData(seq, type);
            });
    };

    handleClose = () => {
        this.setState({
            transactionData: null,
            transactionDialogOpen: false,
            saleDialogOpen: false,
            saleTransactionData: null
        });
    };

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /**
     * Convert Start and End Dates to range for Export filename
     *
     * @param startDate
     * @param endDate
     * @return {string}
     */
    convertDatesForFile(startDate, endDate) {
        let from = new Date(startDate + 'T00:00:00');
        let to = new Date(endDate + 'T00:00:00');
        let fileStr = '';
        let yy = from.getFullYear();
        let mo = String('00' + parseInt(from.getMonth() + 1, 10)).slice(-2);
        let dd = String('00' + from.getDate()).slice(-2);
        fileStr += yy.toString() + mo.toString() + dd.toString() + '-';
        yy = to.getFullYear();
        mo = String('00' + parseInt(to.getMonth() + 1, 10)).slice(-2);
        dd = String('00' + to.getDate()).slice(-2);
        fileStr += yy.toString() + mo.toString() + dd.toString();
        return fileStr;
    }

    /* Table Display ------------------------------ */

    render() {

        const {
            endDate,
            startDate,
            system,
            includeReceipt
        } = this.props;

        const {
            allowLocationSelection,
            summaryList,
            summaryTableData,
            summaryTableHeaders,
            summaryComplete,
            detailComplete,
            detailList,
            detailLoading,
            detailTableData,
            detailTableHeaders,
            detailLocation,
            transactionData,
            transactionDialogOpen,
            creditList,
            creditTableData,
            saleDialogOpen,
            saleTransactionData,
            salePaymentsList,
            saleCreditsList,
            saleProductList,
            saleProductReturnedList,
            saleDiscountsList,
        } = this.state;

        const dateString = this.convertDatesForFile(startDate, endDate);
        const defaultSummarySort = setDefaultTableSort('location_id');
        const defaultDetailSort = setDefaultTableSort({field: 'transaction_date', order: 'DESC'});

        // ReactTable headers and columns for Table
        const iconStyle = (allowLocationSelection) ? tableStyles.tableIconButton : tableStyles.disabledTableIconButton;
        const tableColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'Location Id',
                        accessor: 'location_id',
                        Cell: row => (
                            <span>
                                <span>{row.row.location_id}</span>
                                <DetailIcon
                                    onClick={(e) => this.handleLocationSelect(e, row.row)}
                                    style={iconStyle}
                                />
                            </span>
                        ),
                        style: tableStyles.right
                    },
                    {
                        Header: 'Location Name',
                        accessor: 'location_name',
                    },
                    {
                        Header: 'City',
                        accessor: 'city',
                        style: tableStyles.hide,
                        headerStyle: tableStyles.hide,
                    },
                    {
                        Header: 'State',
                        accessor: 'state',
                    },
                    {
                        Header: 'DBA',
                        accessor: 'dba_name',
                    },
                    {
                        Header: 'Zip Code',
                        accessor: 'zip_code',
                        style: tableStyles.tdMax
                    }
                ]
            }
        ];
        const tableDetailsColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'Payment Id',
                        accessor: 'payment_seq',
                        style: tableStyles.wrapText,
                        Cell: props => (
                            <a style={styles.linkDefault}
                               onClick={(e) => this.handleOpen(e, props.row.payment_seq, props.row.transaction)}
                            >
                                {props.row.payment_seq}
                            </a>
                        )
                    },
                    {
                        Header: 'Sale Id',
                        accessor: 'sale_seq',
                        style: tableStyles.wrapText,
                        Cell: props => (
                            <a style={styles.linkDefault}
                               onClick={(e) => this.handleSaleOpen(e, props.row.sale_seq, props.row.transaction)}
                            >
                                {props.row.sale_seq}
                            </a>
                        )
                    },
                    {
                        Header: 'Transaction Date',
                        accessor: 'transaction_date',
                        Cell: props => (
                            <span>{props.row.transaction_date_string}</span>
                        )
                    },
                    {
                        Header: '',
                        accessor: 'transaction_date_string',
                        style: tableStyles.hide,
                        headerStyle: tableStyles.hide
                    },
                    {
                        Header: 'Register',
                        accessor: 'register_id',
                        style: tableStyles.tdMax
                    },
                    {
                        Header: 'Payment Type',
                        accessor: 'transaction_type'
                    },
                    {
                        Header: 'Transaction',
                        accessor: 'transaction'
                    },
                    {
                        Header: 'Currency',
                        accessor: 'currency',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right
                    },
                    {
                        Header: 'Price',
                        accessor: 'subtotal',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right
                    },
                    {
                        Header: 'Tax',
                        accessor: 'tax',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right
                    },
                    {
                        Header: 'Card Type',
                        accessor: 'card_type',
                    },
                    {
                        Header: 'Last 4',
                        accessor: 'card_last4',
                        style: tableStyles.tdMax,
                    },
                    {
                        Header: 'Card Data Source',
                        accessor: 'card_data_source'
                    },
                    {
                        Header: 'PN Ref',
                        accessor: 'pn_ref',
                    },
                    {
                        Header: 'Auth',
                        accessor: 'authcode',
                        style: tableStyles.tdMax,
                    },
                    {
                        Header: 'Mode',
                        accessor: 'mode',
                        style: tableStyles.tdMax,
                    }
                ]
            }
        ];

        // Assemble Location Detail information
        const locationCity = (detailLocation && detailLocation.city) ? detailLocation.city + ', ' : '';
        const locationState = (detailLocation && detailLocation.state) ? detailLocation.state + '.' : '';

        // Dialog Actions
        const dialogActions = [
            <FlatButton
                label="Close"
                primary={true}
                onClick={this.handleClose}
            />
        ];

        return (
            <div>
                {summaryComplete &&
                    <div className="reports-table-container">

                        {/* Table Header: Transaction Log */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                                {/* Export Transaction Log */}
                                <FlatButton
                                    primary
                                    type="button"
                                    label=""
                                    style={commonStyles.headerButton}
                                >
                                    <ExportIcon style={commonStyles.headerButtonIcon}/>
                                    <CSVLink
                                        data={summaryTableData}
                                        headers={summaryTableHeaders}
                                        filename={'Transaction-Log-Report-' + system + '-' + dateString + '.csv'}
                                        onClick={(e) => this.handleExportClick(e)}
                                        target="_blank"
                                        style={commonStyles.headerButtonLink}
                                    >
                                        Export Transaction Log
                                    </CSVLink>
                                </FlatButton>
                            </div>
                            <h3 className="table-table-header-title">
                                <TableIcon style={commonStyles.summaryIcon}/>
                                <span>Transaction Log</span>
                            </h3>
                        </div>

                        {/* Table: Transaction Log */}
                        <ReactTable
                            key="table-transaction-log-report"
                            data={summaryList}
                            columns={tableColumns}
                            defaultPageSize={10}
                            showPagination={true}
                            defaultSorted={defaultSummarySort}
                            filterable
                            defaultFilterMethod={filterCaseInsensitive}
                            onSortedChange={(e) => this.handleSortOrFilterChange(e, this.reactTable)}
                            onFilteredChange={(e) => this.handleSortOrFilterChange(e, this.reactTable)}
                            ref={(r) => this.reactTable = r}
                            resizable={false}
                            minRows={1}
                            className="-striped"
                        />
                    </div>
                }

                {detailLoading && !detailComplete &&
                    <div style={commonStyles.loaderContainer}>
                        <h4>Generating Report...</h4>
                        <CircularProgress
                            size={24}
                            thickness={2}
                            style={commonStyles.loader}
                        />
                    </div>
                }
                {detailComplete &&
                    <div className="reports-table-container">

                        {/* Table Header: Detail Transaction Logs (Selected Location) */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                                {/* Export Detail Transaction Logs (Selected Location) */}
                                <FlatButton
                                    primary
                                    type="button"
                                    label=""
                                    style={commonStyles.headerButton}
                                >
                                    <ExportIcon style={commonStyles.headerButtonIcon}/>
                                    <CSVLink
                                        data={detailTableData}
                                        headers={detailTableHeaders}
                                        filename={'Transaction-Logs-Report-' + system + '-' + detailLocation.id + '-' + dateString + '.csv'}
                                        onClick={(e) => this.handleExportClick(e)}
                                        target="_blank"
                                        style={commonStyles.headerButtonLink}
                                    >
                                        Export Transaction Logs Report {detailLocation.id}
                                    </CSVLink>
                                </FlatButton>
                            </div>
                            <h3 className="table-table-header-title">
                                <DetailTableIcon style={commonStyles.summaryIcon}/>
                                <span>Transaction Logs by Location {detailLocation.id}</span>
                            </h3>
                            <div style={commonStyles.tableDetail}>
                                Location Name: {detailLocation.name}<br/>
                                DBA: {detailLocation.dba_name}<br/>
                                {locationCity}{locationState}
                            </div>
                        </div>

                        {/* Table: Transaction Logs (Selected Location) */}
                        <ReactTable
                            key="table-transaction-log-detail"
                            data={detailList}
                            columns={tableDetailsColumns}
                            defaultPageSize={10}
                            showPagination={true}
                            defaultSorted={defaultDetailSort}
                            filterable
                            defaultFilterMethod={filterCaseInsensitive}
                            onSortedChange={(e) => this.handleSortOrFilterChange(e, this.detailReactTable)}
                            onFilteredChange={(e) => this.handleSortOrFilterChange(e, this.detailReactTable)}
                            ref={(r) => this.detailReactTable = r}
                            resizable={false}
                            minRows={1}
                            className="-striped overflow no-print"
                        />
                        <ReactTable
                            key="table-transaction-log-detail-print"
                            data={detailTableData}
                            columns={tableDetailsColumns}
                            defaultPageSize={10000}
                            showPagination={false}
                            defaultSorted={defaultDetailSort}
                            defaultSortDesc={true}
                            filterable
                            resizable={false}
                            minRows={1}
                            className="print-only"
                        />
                        <Dialog
                            modal={false}
                            open={transactionDialogOpen}
                            onRequestClose={this.handleClose}
                            contentClassName={"dialogContentTable"}
                            actionsContainerClassName={"dialogPrintOnly"}
                            autoScrollBodyContent={true}
                            actions={dialogActions}
                        >
                            <div style={tableStyles.flexContainerRight}>
                                <ReactToPrint
                                    trigger={() =>
                                        <RaisedButton
                                            primary
                                            type="button"
                                            label="Print Report"
                                            style={tableStyles.printBtn}/>
                                    }
                                    content={() => this.printReportRef}
                                />
                            </div>
                            {!transactionData &&
                                <div className="loader-container">
                                    <h4>Generating Payment Log...</h4>
                                    <CircularProgress
                                        size={24}
                                        thickness={2}
                                        className="loader"
                                    />
                                </div>
                            }
                            {transactionData &&
                                <DialogPaymentSeq
                                    ref={el => (this.printReportRef = el)}
                                    data={transactionData}
                                    creditList={creditList}
                                    creditTableData={creditTableData}
                                    handleSortOrFilterChange={this.handleSortOrFilterChange}
                                />
                            }
                        </Dialog>
                        <Dialog
                            modal={false}
                            open={saleDialogOpen}
                            onRequestClose={this.handleClose}
                            contentClassName={"dialogContentTable"}
                            actionsContainerClassName={"dialogPrintOnly"}
                            autoScrollBodyContent={true}
                            actions={dialogActions}
                        >
                            <div style={tableStyles.flexContainerRight}>
                                {includeReceipt &&
                                    <ReactToPrint
                                        trigger={() =>
                                            <RaisedButton
                                                primary
                                                type="button"
                                                label="Print Receipt"
                                                style={tableStyles.printBtn}
                                            />
                                        }
                                        content={() => this.printReceiptRef}
                                    />
                                }
                                <ReactToPrint
                                    trigger={() =>
                                        <RaisedButton
                                            primary
                                            type="button"
                                            label="Print Report"
                                            style={tableStyles.printBtn}
                                        />
                                    }
                                    content={() => this.printReportRef}
                                />
                            </div>
                            {!saleTransactionData &&
                                <div className="loader-container">
                                    <h4>Generating Sale Log...</h4>
                                    <CircularProgress
                                        size={24}
                                        thickness={2}
                                        className="loader"
                                    />
                                </div>
                            }
                            {saleTransactionData &&
                                <Fragment>
                                    <DialogSaleSeq
                                        ref={e => (this.printReportRef = e)}
                                        data={saleTransactionData}
                                        payments={salePaymentsList}
                                        credits={saleCreditsList}
                                        products={saleProductList}
                                        returns={saleProductReturnedList}
                                        discounts={saleDiscountsList}
                                        handleSortOrFilterChange={this.handleSortOrFilterChange}
                                    />
                                    {includeReceipt &&
                                        <Fragment>
                                            <DialogSaleReceipt
                                                ref={e => (this.printReceiptRef = e)}
                                                data={saleTransactionData}
                                                payments={salePaymentsList}
                                                products={saleProductList}
                                                discounts={saleDiscountsList}
                                            />
                                            <div style={tableStyles.center}>
                                                <ReactToPrint
                                                    trigger={() =>
                                                        <RaisedButton
                                                            primary
                                                            type="button"
                                                            label="Print Receipt"
                                                            style={tableStyles.printBtnCenter}/>
                                                    }
                                                    content={() => this.printReceiptRef}
                                                />
                                            </div>
                                        </Fragment>
                                    }
                                </Fragment>
                            }
                        </Dialog>
                    </div>
                }
                <div ref={(ref) => this.detailPosition = ref}>
                </div>
            </div>
        )
    }
}

TransactionLogReport.propTypes = {
    addField: PropTypes.bool,
    endDate: PropTypes.string,
    endTime: PropTypes.string,
    includeReceipt: PropTypes.bool,
    offset: PropTypes.string.isRequired,
    onDisplayMessage: PropTypes.func,
    onError: PropTypes.func,
    onSummaryComplete: PropTypes.func,
    startDate: PropTypes.string,
    startTime: PropTypes.string,
    system: PropTypes.string,
};

TransactionLogReport.defaultProps = {
    addField: true,
    endDate: null,
    endTime: null,
    includeReceipt: true,
    offset: null,
    startDate: null,
    startTime: null,
};

const enhance = compose(
    connect()
);

export default enhance(TransactionLogReport);



// WEBPACK FOOTER //
// ./src/Fuse/components/TransactionLogReport.js